/**
 * AdminConfigService - Serviço para gerenciar configurações do sistema
 * Apenas usuários admin têm acesso a estas APIs
 * 
 * ATUALIZADO: Adicionados métodos para logs de plataforma (falhas de modelos)
 */
import { api } from '../lib/api';

export const adminConfigService = {
  // ============================================
  // CONFIGURAÇÃO COMPLETA
  // ============================================
  
  async getFullConfig() {
    const response = await api.get('/api/admin/config');
    return response.data;
  },

  async updateFullConfig(configData) {
    const response = await api.put('/api/admin/config', configData);
    return response.data;
  },

  async resetConfig() {
    const response = await api.post('/api/admin/config/reset');
    return response.data;
  },

  // ============================================
  // PLACID SETTINGS
  // ============================================

  async getPlacidSettings() {
    const response = await api.get('/api/admin/config/placid/settings');
    return response.data;
  },

  async updatePlacidSettings(settings) {
    const response = await api.patch('/api/admin/config/placid/settings', settings);
    return response.data;
  },

  // ============================================
  // PLACID TEMPLATES
  // ============================================

  async getPlacidTemplates() {
    const response = await api.get('/api/admin/config/placid/templates');
    return response.data;
  },

  async updatePlacidTemplate(templateId, templateData) {
    const response = await api.put(`/api/admin/config/placid/templates/${templateId}`, templateData);
    return response.data;
  },

  async togglePlacidTemplate(templateId, enabled) {
    const response = await api.patch(`/api/admin/config/placid/templates/${templateId}/toggle`, { enabled });
    return response.data;
  },

  async deletePlacidTemplate(templateId) {
    const response = await api.delete(`/api/admin/config/placid/templates/${templateId}`);
    return response.data;
  },

  // ============================================
  // PLACID TEMPLATES - IMPORTAÇÃO
  // ============================================

  async getAvailablePlacidTemplates({ page, collectionId, titleFilter } = {}) {
    const params = {};
    if (page) params.page = page;
    if (collectionId) params.collection_id = collectionId;
    if (titleFilter) params.title_filter = titleFilter;
    
    const response = await api.get('/api/admin/config/placid/templates/available', { params });
    return response.data;
  },

  async getAvailablePlacidTemplateDetails(templateUuid) {
    const response = await api.get(`/api/admin/config/placid/templates/available/${templateUuid}`);
    return response.data;
  },

  async importPlacidTemplate(templateData) {
    const response = await api.post('/api/admin/config/placid/templates/import', templateData);
    return response.data;
  },

  async syncTemplateLayers(templateId) {
    const response = await api.post(`/api/admin/config/placid/templates/${templateId}/sync-layers`);
    return response.data;
  },

  // ============================================
  // IMAGE GENERATORS (Fallback Chain)
  // ============================================

  async getImageGenerators() {
    const response = await api.get('/api/admin/config/generators');
    return response.data;
  },

  async updateImageGenerators(generators) {
    const response = await api.put('/api/admin/config/generators', generators);
    return response.data;
  },

  async toggleImageGenerator(generatorId, enabled) {
    const response = await api.patch(`/api/admin/config/generators/${generatorId}/toggle`, { enabled });
    return response.data;
  },

  async reorderGenerators(orderList) {
    const response = await api.patch('/api/admin/config/generators/reorder', { order: orderList });
    return response.data;
  },

  // ============================================
  // MODEL SELECTOR (AI Selection)
  // ============================================

  async getModelSelectorConfig() {
    const response = await api.get('/api/admin/config/model-selector');
    return response.data;
  },

  async updateModelSelectorConfig(config) {
    const response = await api.patch('/api/admin/config/model-selector', config);
    return response.data;
  },

  // ============================================
  // AGENT CONFIG
  // ============================================

  async getAgentConfig() {
    const response = await api.get('/api/admin/config/agent');
    return response.data;
  },

  async updateAgentParams(params) {
    const response = await api.patch('/api/admin/config/agent/config', params);
    return response.data;
  },

  async updateAgentPrompts(prompts) {
    const response = await api.patch('/api/admin/config/agent/prompts', prompts);
    return response.data;
  },

  // ============================================
  // POPULAR FONTS
  // ============================================

  async getPopularFonts() {
    const response = await api.get('/api/admin/config/fonts');
    return response.data;
  },

  async updatePopularFonts(fonts) {
    const response = await api.put('/api/admin/config/fonts', { fonts });
    return response.data;
  },

  // ============================================
  // 🆕 PLATFORM LOGS - FALHAS DE MODELOS
  // ============================================

  /**
   * Lista falhas de modelos de geração com filtros
   * @param {Object} params - Filtros
   * @param {string} params.model_name - Filtrar por modelo específico
   * @param {number} params.hours_ago - Período em horas (padrão: 24)
   * @param {number} params.limit - Limite de resultados (padrão: 100)
   */
  async getModelFailures({ model_name, hours_ago = 24, limit = 100 } = {}) {
    const params = { hours_ago, limit };
    if (model_name) params.model_name = model_name;
    
    const response = await api.get('/api/admin/logs/model-failures', { params });
    return response.data;
  },

  /**
   * Resumo agregado de falhas por modelo
   * @param {number} hoursAgo - Período em horas
   */
  async getModelFailureSummary(hoursAgo = 24) {
    const response = await api.get('/api/admin/logs/model-failures/summary', {
      params: { hours_ago: hoursAgo }
    });
    return response.data;
  },

  /**
   * Estatísticas detalhadas de falhas (resumo + recentes + modelo mais problemático)
   * @param {number} hoursAgo - Período em horas
   */
  async getModelFailureStats(hoursAgo = 24) {
    const response = await api.get('/api/admin/logs/model-failures/stats', {
      params: { hours_ago: hoursAgo }
    });
    return response.data;
  },

  /**
   * Lista eventos genéricos da plataforma
   * @param {Object} params - Filtros
   */
  async getPlatformEvents({ event_type, severity, hours_ago = 24, limit = 50 } = {}) {
    const params = { hours_ago, limit };
    if (event_type) params.event_type = event_type;
    if (severity) params.severity = severity;
    
    const response = await api.get('/api/admin/logs/events', { params });
    return response.data;
  },

  // ============================================
  // LAB — LAYOUT AGENTS
  // ============================================

  async getLabLayoutAgents() {
    const response = await api.get('/api/admin/config/lab/layout-agents');
    return response.data;
  },

  async updateLabLayoutAgent(layoutId, data) {
    const response = await api.put(`/api/admin/config/lab/layout-agents/${layoutId}`, data);
    return response.data;
  },

  async toggleLabLayoutAgent(layoutId, enabled) {
    const response = await api.patch(`/api/admin/config/lab/layout-agents/${layoutId}/toggle`, { enabled });
    return response.data;
  },

  async deleteLabLayoutAgent(layoutId) {
    const response = await api.delete(`/api/admin/config/lab/layout-agents/${layoutId}`);
    return response.data;
  },

  // ============================================
  // LAB — PLANNER CONFIG
  // ============================================

  async getLabPlannerConfig() {
    const response = await api.get('/api/admin/config/lab/planner');
    return response.data;
  },

  async updateLabPlannerConfig(config) {
    const response = await api.patch('/api/admin/config/lab/planner', config);
    return response.data;
  },

  // ============================================
  // LAB — LAYOUT SYSTEM PROMPT
  // ============================================

  async getLabLayoutSystemPrompt() {
    const response = await api.get('/api/admin/config/lab/layout-system-prompt');
    return response.data;
  },

  async updateLabLayoutSystemPrompt(prompt) {
    const response = await api.patch('/api/admin/config/lab/layout-system-prompt', { prompt });
    return response.data;
  },

  // ============================================
  // LAB — REACT LAYOUT AGENTS (provider react_render)
  // ============================================

  async getLabReactLayoutAgents() {
    const response = await api.get('/api/admin/config/lab/react/layout-agents');
    return response.data;
  },

  async updateLabReactLayoutAgent(layoutId, data) {
    const response = await api.put(`/api/admin/config/lab/react/layout-agents/${layoutId}`, data);
    return response.data;
  },

  async toggleLabReactLayoutAgent(layoutId, enabled) {
    const response = await api.patch(`/api/admin/config/lab/react/layout-agents/${layoutId}/toggle`, { enabled });
    return response.data;
  },

  async deleteLabReactLayoutAgent(layoutId) {
    const response = await api.delete(`/api/admin/config/lab/react/layout-agents/${layoutId}`);
    return response.data;
  },

  async getLabReactSystemPrompt() {
    const response = await api.get('/api/admin/config/lab/react/system-prompt');
    return response.data;
  },

  async updateLabReactSystemPrompt(prompt) {
    const response = await api.patch('/api/admin/config/lab/react/system-prompt', { prompt });
    return response.data;
  },

  // ============================================
  // PROMPT DA GERAÇÃO DE IMAGENS (gpt-image planner)
  // ============================================
  // Dois slots: produção (afeta clientes) e rascunho (só o Lab).

  async getImagePlanner() {
    const response = await api.get('/api/admin/config/image-planner');
    return response.data;
  },

  async updateImagePlanner(payload) {
    const response = await api.patch('/api/admin/config/image-planner', payload);
    return response.data;
  },

  async getImagePlannerDraft() {
    const response = await api.get('/api/admin/config/image-planner/draft');
    return response.data;
  },

  async updateImagePlannerDraft(payload) {
    const response = await api.patch('/api/admin/config/image-planner/draft', payload);
    return response.data;
  },

  async promoteImagePlannerDraft() {
    const response = await api.post('/api/admin/config/image-planner/draft/promote');
    return response.data;
  },

  async resetImagePlannerDraft() {
    const response = await api.post('/api/admin/config/image-planner/draft/reset');
    return response.data;
  },

  // ============================================
  // LAB — VARIÁVEIS DISPONÍVEIS
  // ============================================

  async getLabVariables() {
    const response = await api.get('/api/admin/config/lab/variables');
    return response.data;
  },

  // ============================================
  // LAB — NICHOS
  // ============================================

  async getLabNiches() {
    const response = await api.get('/api/admin/config/lab/niches');
    return response.data;
  },

  async updateLabNiche(nicheId, data) {
    const response = await api.put(`/api/admin/config/lab/niches/${nicheId}`, data);
    return response.data;
  },

  async toggleLabNiche(nicheId, enabled) {
    const response = await api.patch(`/api/admin/config/lab/niches/${nicheId}/toggle`, { enabled });
    return response.data;
  },

  async deleteLabNiche(nicheId) {
    const response = await api.delete(`/api/admin/config/lab/niches/${nicheId}`);
    return response.data;
  },

  async regenerateLabNichePreviews(nicheId) {
    const response = await api.post(`/api/admin/config/lab/niches/${nicheId}/regenerate-previews`);
    return response.data;
  },

  // ============================================
  // HELPERS
  // ============================================

  formatGeneratorStatus(generator) {
    return {
      ...generator,
      statusText: generator.enabled ? 'Ativo' : 'Inativo',
      statusColor: generator.enabled ? 'green' : 'gray'
    };
  },

  formatTemplateStatus(template) {
    return {
      ...template,
      statusText: template.enabled ? 'Ativo' : 'Inativo',
      statusColor: template.enabled ? 'green' : 'gray'
    };
  },

  // ============================================
  // CREATOMATE SPLIT CONFIG
  // ============================================

  async getCreatomateSplit() {
    const response = await api.get('/api/admin/config/creatomate/split');
    return response.data;
  },

  async updateCreatomateSplit(settings) {
    const response = await api.patch('/api/admin/config/creatomate/split', settings);
    return response.data;
  },

  // ============================================
  // TEMPLATED.IO TEMPLATES
  // ============================================

  async getTemplatedTemplates() {
    const response = await api.get('/api/admin/config/templated/templates');
    return response.data;
  },

  async getAvailableTemplatedTemplates() {
    const response = await api.get('/api/admin/config/templated/available');
    return response.data;
  },

  async importTemplatedTemplate(templateId, templateData) {
    const response = await api.put(`/api/admin/config/templated/templates/${templateId}`, templateData);
    return response.data;
  },

  async toggleTemplatedTemplate(templateId, enabled) {
    const response = await api.patch(`/api/admin/config/templated/templates/${templateId}/toggle`, { enabled });
    return response.data;
  },

  async deleteTemplatedTemplate(templateId) {
    const response = await api.delete(`/api/admin/config/templated/templates/${templateId}`);
    return response.data;
  }
};

export default adminConfigService;